import { Button, Modal, useActionsBtnContext, useErrorState } from '@finbeat/react-toolkit';
import {
    deleteClient,
    getClient,
    getClientDocumentsInfo,
    getClientPaymentsInfo,
    getHistory,
    getOrders,
} from 'axios/clientApi';
import ClientFilesTab from 'components/Workplaces/Order/OrderInfo/Tabs/ClientFilesTab';
import ClientTab from 'components/Workplaces/Order/OrderInfo/Tabs/ClientTab';
import PaymentsTab from 'components/Workplaces/Order/OrderInfo/Tabs/PaymentsTab';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { ClientTable } from 'models/api/client';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import {
    ModalBody,
    ModalFooter,
    ModalHeader,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
} from 'reactstrap';
import { useClientWorkplaceState } from './Clients';
import HistoryTab from './Tabs/HistoryTab';
import OrdersTab from './Tabs/OrdersTab';

type ClientChangeModalProps = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
    id?: number | string;
};

const ClientChangeModal = ({ isOpen = false, toggle, id }: ClientChangeModalProps) => {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('0');
    const { t } = useTranslation();
    const { handleRequestError } = useErrorState();
    const { addRequest } = useActionsBtnContext();
    const api = useApi();
    const { hasRole } = useAuth();
    const initialItem: ClientTable = {
        id: id ?? 0,
        brandName: null,
        phoneNumber: null,
        email: null,
        fio: null,
        birthday: null,
        docNum: null,
        isBlocked: false,
        collectionStatus: null
    };

    const { selectedItem } = useClientWorkplaceState();
    const [client, setClient] = useState<ClientTable>({
        ...initialItem,
        ...selectedItem,
    });
    useEffect(() => setClient({ ...initialItem, ...selectedItem }), [selectedItem]);
    useEffect(() => {
        id && setClient({ ...initialItem, ...selectedItem, id: id });
    }, [id]);

    const deleteItemMutation = useMutation(() => deleteClient(api, client.id), {
        onSuccess: () => {
            queryClient.invalidateQueries([['orders']]);
            queryClient.invalidateQueries([['clients']]);
            toggle(false);
        },
        onError: (e) => handleRequestError(e),
    });

    const className = 'questionary-modal';
    return (
        <Modal
            size="xl"
            isOpen={isOpen}
            toggle={() => toggle(false)}
            scrollable
            className="clients__modal"
        >
            <ModalHeader toggle={() => toggle(false)}>
                {t('BackOfficeClient')} #{client.id}
            </ModalHeader>
            <ModalBody className="custom-scrollbar">
                <Nav className={`${className}__nav`} tabs>
                    <NavItem>
                        <NavLink
                            href="#"
                            onClick={() => setActiveTab('0')}
                            active={activeTab === '0'}
                        >
                            <Trans i18nKey="BackOfficeInfo" />
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            href="#"
                            onClick={() => setActiveTab('1')}
                            active={activeTab === '1'}
                        >
                            <Trans i18nKey="BackOfficeOrders" />
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            href="#"
                            onClick={() => setActiveTab('2')}
                            active={activeTab === '2'}
                        >
                            <Trans i18nKey="BackOfficePayments" />
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            href="#"
                            onClick={() => setActiveTab('3')}
                            active={activeTab === '3'}
                        >
                            <Trans i18nKey="BackOfficeDocuments" />
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            href="#"
                            onClick={() => setActiveTab('4')}
                            active={activeTab === '4'}
                        >
                            <Trans i18nKey="BackOfficeHistory" />
                        </NavLink>
                    </NavItem>
                </Nav>
                <TabContent activeTab={activeTab}>
                    <TabPane tabId="0">
                        <ClientTab
                            clientInfoQueryFn={() => getClient(api, client.id)}
                            isEditable={hasRole('clientsUpdateData')}
                            isOpen={activeTab === '0'}
                            clientId={client.id}
                        />
                    </TabPane>
                    <TabPane tabId="1">
                        <OrdersTab
                            clientId={client.id}
                            isShown={activeTab === '1'}
                            query={() => getOrders(api, client.id)}
                        />
                    </TabPane>
                    <TabPane tabId="2">
                        <PaymentsTab
                            paymentsQueryFn={() => getClientPaymentsInfo(api, client.id)}
                            isOpen={activeTab === '2'}
                        />
                    </TabPane>
                    <TabPane tabId="3">
                        <ClientFilesTab
                            documentsQueryFn={() => getClientDocumentsInfo(api, client.id)}
                            isOpen={activeTab === '3'}
                            canRequestingDocs={false}
                            isEditable={false}
                            clientId={client.id}
                        />
                    </TabPane>
                    <TabPane tabId="4">
                        <HistoryTab
                            clientId={client.id}
                            isShown={activeTab === '4'}
                            collectionStatus={client.collectionStatus}
                            query={() => getHistory(api, client.id)}
                        />
                    </TabPane>
                </TabContent>
            </ModalBody>
            <ModalFooter>
                {hasRole('clientsDelete') && (
                    <Button
                        color="danger"
                        onClick={() =>
                            addRequest({
                                content: t('BackOfficeAreYouSure'),
                                confirmCallback: (res) => {
                                    if (res && client.id) {
                                        deleteItemMutation.mutate();
                                    }
                                },
                            })
                        }
                    >
                        <Trans i18nKey="BackOfficeDelete" />
                    </Button>
                )}
                <Button color="secondary" onClick={() => toggle(false)}>
                    <Trans i18nKey="BackOfficeCancel" />
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ClientChangeModal;
